/**
 * User Hooks
 * React Query hooks for user/office/court management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { usersApi, officesApi, courtsApi } from '@/api'
import type {
  UserCreate,
  UserUpdate,
  UserFilters,
  UserPasswordReset,
  OfficeCreate,
  CourtCreate,
} from '@/types/user'
import type { PaginationParams } from '@/types/common'

// Query keys
export const userKeys = {
  all: ['users'] as const,
  lists: () => [...userKeys.all, 'list'] as const,
  list: (filters?: UserFilters, pagination?: PaginationParams) =>
    [...userKeys.lists(), { filters, pagination }] as const,
  details: () => [...userKeys.all, 'detail'] as const,
  detail: (id: string) => [...userKeys.details(), id] as const,
}

export const roleKeys = {
  all: ['roles'] as const,
}

export const officeKeys = {
  all: ['offices'] as const,
  lists: () => [...officeKeys.all, 'list'] as const,
  details: () => [...officeKeys.all, 'detail'] as const,
  detail: (id: number) => [...officeKeys.details(), id] as const,
}

export const courtKeys = {
  all: ['courts'] as const,
  lists: () => [...courtKeys.all, 'list'] as const,
  details: () => [...courtKeys.all, 'detail'] as const,
  detail: (id: number) => [...courtKeys.details(), id] as const,
}

// ============================================================================
// USER HOOKS
// ============================================================================

export function useUsers(filters?: UserFilters, pagination?: PaginationParams) {
  return useQuery({
    queryKey: userKeys.list(filters, pagination),
    queryFn: () => usersApi.getAll(filters, pagination),
  })
}

export function useUser(id: string) {
  return useQuery({
    queryKey: userKeys.detail(id),
    queryFn: () => usersApi.getById(id),
    enabled: !!id,
  })
}

export function useCreateUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: UserCreate) => usersApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() })
    },
  })
}

export function useUpdateUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UserUpdate }) =>
      usersApi.update(id, data),
    onSuccess: (updatedUser) => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() })
      queryClient.invalidateQueries({ queryKey: userKeys.detail(updatedUser.id) })
    },
  })
}

export function useDeleteUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => usersApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() })
    },
  })
}

export function useResetUserPassword() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UserPasswordReset }) =>
      usersApi.resetPassword(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() })
    },
  })
}

export function useRoles() {
  return useQuery({
    queryKey: roleKeys.all,
    queryFn: () => usersApi.getRoles(),
    staleTime: Infinity, // Roles don't change often
  })
}

// ============================================================================
// OFFICE HOOKS
// ============================================================================

export function useOffices() {
  return useQuery({
    queryKey: officeKeys.lists(),
    queryFn: () => officesApi.getAll(),
  })
}

export function useOffice(id: number) {
  return useQuery({
    queryKey: officeKeys.detail(id),
    queryFn: () => officesApi.getById(id),
    enabled: !!id,
  })
}

export function useCreateOffice() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: OfficeCreate) => officesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: officeKeys.lists() })
    },
  })
}

export function useUpdateOffice() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<OfficeCreate> }) =>
      officesApi.update(id, data),
    onSuccess: (updatedOffice) => {
      queryClient.invalidateQueries({ queryKey: officeKeys.lists() })
      queryClient.invalidateQueries({ queryKey: officeKeys.detail(updatedOffice.id) })
    },
  })
}

export function useDeleteOffice() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => officesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: officeKeys.lists() })
    },
  })
}

// ============================================================================
// COURT HOOKS
// ============================================================================

export function useCourts() {
  return useQuery({
    queryKey: courtKeys.lists(),
    queryFn: () => courtsApi.getAll(),
  })
}

export function useCourt(id: number) {
  return useQuery({
    queryKey: courtKeys.detail(id),
    queryFn: () => courtsApi.getById(id),
    enabled: !!id,
  })
}

export function useCreateCourt() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CourtCreate) => courtsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: courtKeys.lists() })
    },
  })
}

export function useUpdateCourt() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<CourtCreate> }) =>
      courtsApi.update(id, data),
    onSuccess: (updatedCourt) => {
      queryClient.invalidateQueries({ queryKey: courtKeys.lists() })
      queryClient.invalidateQueries({ queryKey: courtKeys.detail(updatedCourt.id) })
    },
  })
}

export function useDeleteCourt() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => courtsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: courtKeys.lists() })
    },
  })
}
